/**
 * South African public holidays.
 *
 * The Public Holidays Act 36 of 1994 fixes twelve days. Ten of them are on
 * fixed dates; Good Friday and Family Day move with Easter. Section 2(1) adds
 * the rule that does most of the damage to naive implementations:
 *
 *   "whenever any public holiday falls on a Sunday, the Monday following on it
 *    shall be a public holiday"                        — gov.za, quoting the Act
 *
 * So 2026 has thirteen days off, not twelve: National Women's Day fell on
 * Sunday 9 August, and Monday 10 August is a public holiday as well. Both are
 * returned here — the Sunday because it is one, the Monday because the Act
 * makes it one.
 *
 * Why this matters beyond the calendar: BCEA section 18 pays a worked public
 * holiday at roughly double, and until this file existed Shifty did not know
 * what a public holiday was. Every shift worked on one has been priced as an
 * ordinary day.
 *
 * Not handled here, deliberately:
 *
 *   * Days declared by proclamation for a single year — election days, and the
 *     days the President has declared from time to time. They are real public
 *     holidays and they cannot be computed, so they arrive as `extra` from
 *     whoever is calling. See `holidaysFor`.
 *   * Section 2(2)'s exchange, where an employer and employee agree to swap a
 *     public holiday for another day. That is an agreement between two people,
 *     not a property of the calendar.
 */

export interface PublicHoliday {
  /** ISO date, local. */
  date: string;
  name: string;
  /** Set when this day is a holiday only because the real one fell on a
   *  Sunday. Carries the name of that Sunday, for showing on a payslip. */
  observedFor?: string;
}

/** Anonymous Gregorian computus — Easter Sunday for a year in the Gregorian
 *  calendar. Meeus/Jones/Butcher; it is arithmetic, not a table, so it does
 *  not run out. */
export function easterSunday(year: number): string {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3 = March, 4 = April
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return iso(year, month, day);
}

const iso = (y: number, m: number, d: number) =>
  `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

/** Local midnight for an ISO date, matching core/calendar's fromISODate. */
const at = (isoDate: string) => {
  const [y, m, d] = isoDate.split("-").map(Number);
  return new Date(y!, m! - 1, d!);
};

const plusDays = (isoDate: string, n: number) => {
  const d = at(isoDate);
  d.setDate(d.getDate() + n);
  return iso(d.getFullYear(), d.getMonth() + 1, d.getDate());
};

/**
 * The twelve, plus any Monday the Sunday rule adds.
 *
 * Returned sorted by date. A day that is a holiday twice over — Christmas and
 * a Monday observed for a Sunday holiday, say — appears once.
 */
export function publicHolidays(year: number): PublicHoliday[] {
  const easter = easterSunday(year);

  const fixed: PublicHoliday[] = [
    { date: iso(year, 1, 1), name: "New Year's Day" },
    { date: iso(year, 3, 21), name: "Human Rights Day" },
    { date: plusDays(easter, -2), name: "Good Friday" },
    { date: plusDays(easter, 1), name: "Family Day" },
    { date: iso(year, 4, 27), name: "Freedom Day" },
    { date: iso(year, 5, 1), name: "Workers' Day" },
    { date: iso(year, 6, 16), name: "Youth Day" },
    { date: iso(year, 8, 9), name: "National Women's Day" },
    { date: iso(year, 9, 24), name: "Heritage Day" },
    { date: iso(year, 12, 16), name: "Day of Reconciliation" },
    { date: iso(year, 12, 25), name: "Christmas Day" },
    { date: iso(year, 12, 26), name: "Day of Goodwill" },
  ];

  const byDate = new Map(fixed.map((h) => [h.date, h]));

  // Section 2(1). Good Friday and Family Day can never land on a Sunday, and
  // Christmas and the Day of Goodwill can both shift, so this runs over the
  // whole list rather than a chosen few.
  for (const h of fixed) {
    if (at(h.date).getDay() !== 0) continue;
    const monday = plusDays(h.date, 1);
    if (!byDate.has(monday)) {
      byDate.set(monday, { date: monday, name: `${h.name} observed`, observedFor: h.name });
    }
  }

  return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
}

/* ---------- days that no rule can produce ---------- */

/**
 * Proclaimed days, held once for the whole process.
 *
 * An election day cannot be computed — somebody proclaims it — so the list has
 * to come from the database. Eleven places price a day, and threading a list
 * through all of them means the twelfth forgets. That is not hypothetical: the
 * register went months taking `dayLookup`'s default because nobody passed it a
 * work pattern, and nothing looked wrong.
 *
 * So this is the one piece of mutable state in core, and it is deliberate. It
 * starts empty, which is exactly the behaviour before it existed, and each
 * client fills it once at startup. Passing `declared` explicitly still wins,
 * which is how the tests stay hermetic.
 */
let declared: PublicHoliday[] = [];

/** Called once per client, after the calendar is read. */
export function useDeclaredHolidays(list: PublicHoliday[]): void {
  declared = [...list].sort((a, b) => a.date.localeCompare(b.date));
}

/** What is currently loaded. Every rule that prices a day falls back to this. */
export function declaredHolidays(): PublicHoliday[] {
  return declared;
}

/**
 * Holidays covering every year the given dates touch, plus any declared days.
 * `extra` defaults to whatever the client loaded — see useDeclaredHolidays.
 */
export function holidaysFor(years: number[], extra: PublicHoliday[] = declared): Map<string, PublicHoliday> {
  const out = new Map<string, PublicHoliday>();
  for (const y of new Set(years)) {
    for (const h of publicHolidays(y)) out.set(h.date, h);
  }
  // Declared days win, so a proclamation can name a day the rules did not.
  for (const h of extra) out.set(h.date, h);
  return out;
}

/** The holiday on that date, if it is one. */
export function holidayOn(
  isoDate: string,
  extra: PublicHoliday[] = declared,
): PublicHoliday | undefined {
  const year = Number(isoDate.slice(0, 4));
  return holidaysFor([year], extra).get(isoDate);
}

export const isPublicHoliday = (isoDate: string, extra: PublicHoliday[] = declared) =>
  holidayOn(isoDate, extra) !== undefined;
